import { ZendeskJwtService } from 'us-web-services';

type ZendeskAPI = 'messenger' | 'webWidget';
type ZendeskActions = 'open' | 'close' | 'hide' | 'show' | 'loginUser' | 'logoutUser' | 'updateSettings' | 'updatePath' | 'identify';

declare global {
  interface Window {
    zE?: (
      api: ZendeskAPI,
      action: ZendeskActions,
      onToken?: (callback: (jwt: string) => void) => void,
      onError?: (error: unknown) => void
    ) => void;
    zESettings?: {
      webWidget?: {
        offset?: {
          horizontal?: string;
          vertical?: string;
        }
      }
    }
  }
}

class ZendeskService {
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    if (this.initialized) return Promise.resolve();

    this.initPromise = new Promise((resolve, reject) => {
      const existingScript = document.getElementById('ze-snippet');

      if (existingScript) {
        this.initialized = true;
        resolve();
        return;
      }

      const zendeskKey = process.env.REACT_APP_ZENDESK_KEY;
      const script = document.createElement('script');
      script.id = 'ze-snippet';
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
      script.async = true;

      script.onload = (): void  => {
        this.initialized = true;
        resolve();
      };

      script.onerror = (): void  => {
        this.initPromise = null;
        reject(new Error('Failed to load Zendesk script'));
      };

      document.body.appendChild(script);
    });

    return this.initPromise;
  }

  show = (): void => {
    window.zE?.('webWidget', 'show');
  }

  hide(): void {
    window.zE?.('webWidget', 'hide');
  }

  open(): void {
    window.zE?.('webWidget', 'open');
  }

  close(): void {
    window.zE?.('webWidget', 'close');
  }

  async identifyUser(parentId: number): Promise<void> {
    await ZendeskJwtService.createUser(parentId);

    window.zE?.(
      'messenger',
      'loginUser',
        async (callback: (jwt: string) => void) => {
          try {
            const jwtResponse: { data: string } = (await ZendeskJwtService.getJwt(parentId) as { data: string });
            callback(jwtResponse?.data);
          } catch (err) {
            console.error('Failed to fetch JWT', err);
          }
        },
        async (error) => {
          if (!error) await ZendeskJwtService.syncUser(parentId);
        }
    );
  }

  logoutUser(): void {
    window.zE?.('messenger', 'logoutUser');
  }
}

export const zendesk = new ZendeskService();
